import React, { useState, useRef } from "react"
import { Upload, Loader2, X } from "lucide-react"
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "src/components/ui/dialog"
import { Button } from "src/components/ui/button"
import { Input } from "src/components/ui/input"
import { Label } from "src/components/ui/label"
import { Switch } from "src/components/ui/switch"
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "src/components/ui/select"
import { useToast } from "src/components/ui/toast"
import { useUploadDocument } from "../hooks/useDocuments"
import { CATEGORY_LABELS, formatSize } from "../constants"

// Upload one or more files with shared metadata (category, expiry, etc.).
// `categories` constrains the category dropdown to the relevant group; pass
// the full list for the org library. `scope` is { type, id } or null.
export function UploadDocumentModal({
  open, onOpenChange, scope, categories = Object.keys(CATEGORY_LABELS), onUploaded,
}) {
  const { toast } = useToast()
  const { upload, uploading } = useUploadDocument(scope)
  const fileRef = useRef(null)

  const [files, setFiles] = useState([])
  const [title, setTitle] = useState("")
  const [category, setCategory] = useState("")
  const [expiresAt, setExpiresAt] = useState("")
  const [shareable, setShareable] = useState(false)

  function reset() {
    setFiles([]); setTitle(""); setCategory(""); setExpiresAt(""); setShareable(false)
    if (fileRef.current) fileRef.current.value = ""
  }

  function handleClose(next) {
    if (!next) reset()
    onOpenChange(next)
  }

  async function handleSubmit() {
    if (files.length === 0) {
      toast({ variant: "destructive", title: "Pick at least one file" })
      return
    }
    const MAX_FILE_SIZE = 125 * 1024 * 1024 // 125 MB
    const largeFile = files.find((f) => f.size > MAX_FILE_SIZE)
    if (largeFile) {
      toast({
        variant: "destructive",
        title: "File too large",
        description: `"${largeFile.name}" exceeds the 125 MB limit.`
      })
      return
    }
    const metadata = {
      category: category || undefined,
      expires_at: expiresAt || undefined,
      is_shareable_with_customer: shareable || undefined,
    }
    try {
      for (let i = 0; i < files.length; i++) {
        // Only the first file carries the explicit title when multiple are sent.
        await upload(files[i], { ...metadata, title: files.length === 1 ? title || undefined : undefined })
      }
      toast({ title: files.length > 1 ? `${files.length} documents uploaded` : "Document uploaded" })
      reset()
      onOpenChange(false)
      onUploaded?.()
    } catch (err) {
      toast({ variant: "destructive", title: "Upload failed", description: err.response?.data?.errors?.[0] })
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Upload document</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Dropzone */}
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); setFiles(Array.from(e.dataTransfer.files)) }}
            className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-300 bg-slate-50/50 py-8 text-center"
          >
            <Upload className="h-6 w-6 text-slate-400" />
            <div className="text-sm text-slate-600">Drag &amp; drop files here, or</div>
            <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => fileRef.current?.click()}>
              <Upload className="h-3.5 w-3.5" /> Browse files
            </Button>
            <input
              ref={fileRef} type="file" multiple className="hidden"
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
            />
          </div>

          {/* Selected files */}
          {files.length > 0 && (
            <ul className="space-y-1.5">
              {files.map((f, i) => (
                <li key={i} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
                  <span className="truncate text-slate-700">{f.name}</span>
                  <span className="flex items-center gap-2 text-xs text-slate-400">
                    {formatSize(f.size)}
                    <Button type="button" variant="ghost" size="icon" className="h-auto w-auto p-0 hover:bg-transparent" onClick={() => setFiles(files.filter((_, idx) => idx !== i))}>
                      <X className="h-3.5 w-3.5 hover:text-slate-700" />
                    </Button>
                  </span>
                </li>
              ))}
            </ul>
          )}

          {files.length === 1 && (
            <div className="space-y-1.5">
              <Label htmlFor="doc-title">Title (optional)</Label>
              <Input id="doc-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Defaults to the file name" />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger><SelectValue placeholder="Uncategorized" /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c}>{CATEGORY_LABELS[c] || c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="doc-expiry">Expires on (optional)</Label>
              <Input id="doc-expiry" type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
            </div>
          </div>

          <div className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2.5">
            <div>
              <div className="text-sm font-medium text-slate-800">Shareable with customer</div>
              <div className="text-xs text-slate-400">Allow sending this document to leads / buyers</div>
            </div>
            <Switch checked={shareable} onCheckedChange={setShareable} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={uploading}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={uploading} className="gap-1.5">
            {uploading && <Loader2 className="h-4 w-4 animate-spin" />}
            {uploading ? "Uploading…" : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
